import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { toast } from 'sonner';
import { ClipboardCheck, Clock, CheckCircle, Mail, ArrowLeft } from 'lucide-react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

const Login = () => {
  const navigate = useNavigate();
  const { login, register } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [pendingRequest, setPendingRequest] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotEmailSent, setForgotEmailSent] = useState(false);
  
  const [loginForm, setLoginForm] = useState({ email: '', password: '' });
  const [registerForm, setRegisterForm] = useState({ 
    email: '', 
    password: '', 
    confirmPassword: '',
    name: ''
  });

  const handleLogin = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    try {
      await login(loginForm.email, loginForm.password);
      toast.success('Welcome back!');
      navigate('/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Login failed');
    } finally {
      setIsLoading(false);
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    if (!forgotEmail) {
      toast.error('Please enter your email address');
      return;
    }
    setIsLoading(true);
    try {
      await axios.post(`${API}/api/auth/forgot-password`, { email: forgotEmail });
      setForgotEmailSent(true);
      toast.success('Password reset link sent!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send reset email');
    } finally {
      setIsLoading(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    if (registerForm.password !== registerForm.confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }
    setIsLoading(true);
    try {
      const response = await register(registerForm.email, registerForm.password, registerForm.name);
      if (response.status === 'pending') {
        setPendingRequest(true);
        toast.success('Access request submitted!');
      } else if (response.status === 'approved') {
        toast.success('Account created! Please login.');
        setLoginForm({ email: registerForm.email, password: '' });
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Registration failed');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex">
      {/* Left side - Image */}
      <div 
        className="hidden lg:flex lg:w-1/2 relative bg-cover bg-center"
        style={{ 
          backgroundImage: `url(https://images.unsplash.com/photo-1749478072094-d21cb929490c?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDN8MHwxfHNlYXJjaHwyfHxjb21tZXJjaWFsJTIwa2l0Y2hlbiUyMGludGVyaW9yJTIwc3RhaW5sZXNzJTIwc3RlZWwlMjBjbGVhbnxlbnwwfHx8fDE3NzAxMzQ1NzR8MA&ixlib=rb-4.1.0&q=85)` 
        }}
      >
        <div className="absolute inset-0 bg-slate-900/70" />
        <div className="relative z-10 flex flex-col justify-center p-12 text-white">
          <div className="flex items-center gap-3 mb-8">
            <img 
              src="/tka-logo.png" 
              alt="TKA Logo" 
              className="w-16 h-16 rounded-xl object-contain bg-white p-1"
            />
            <span className="text-2xl font-bold font-['Chivo']">TKA Audit Pro</span>
          </div>
          <h1 className="text-4xl font-bold font-['Chivo'] mb-4 leading-tight">
            Comprehensive EHS Auditing for Food Safety Excellence
          </h1>
          <p className="text-lg text-slate-300 leading-relaxed">
            Streamline your environmental health & safety audits with The Kitchen Affairs&apos; professional-grade platform. 
            Conduct thorough inspections, track corrective actions, and maintain compliance with ease.
          </p>
          <div className="mt-8 flex gap-6">
            <div className="flex items-center gap-2">
              <ClipboardCheck className="w-5 h-5 text-emerald-400" />
              <span className="text-sm">80+ Audit Points</span>
            </div>
            <div className="flex items-center gap-2">
              <ClipboardCheck className="w-5 h-5 text-emerald-400" />
              <span className="text-sm">Photo Evidence</span>
            </div>
            <div className="flex items-center gap-2">
              <ClipboardCheck className="w-5 h-5 text-emerald-400" />
              <span className="text-sm">Action Tracking</span>
            </div>
          </div>
        </div>
      </div>

      {/* Right side - Auth forms */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-8 bg-slate-50">
        <div className="w-full max-w-md">
          <div className="lg:hidden flex items-center gap-3 mb-8 justify-center">
            <img 
              src="/tka-logo.png" 
              alt="TKA Logo" 
              className="w-12 h-12 rounded-lg object-contain"
            />
            <span className="text-xl font-bold font-['Chivo'] text-slate-900">TKA Audit Pro</span>
          </div>

          <Card className="border-0 shadow-xl">
            <Tabs defaultValue="login" className="w-full">
              <CardHeader className="pb-4">
                <TabsList className="grid w-full grid-cols-2" data-testid="auth-tabs">
                  <TabsTrigger value="login" data-testid="login-tab">Sign In</TabsTrigger>
                  <TabsTrigger value="register" data-testid="register-tab">Register</TabsTrigger>
                </TabsList>
              </CardHeader>

              <CardContent>
                <TabsContent value="login" className="mt-0">
                  <CardTitle className="text-2xl font-['Chivo'] mb-2">Welcome back</CardTitle>
                  <CardDescription className="mb-6">Enter your credentials to access your account</CardDescription>
                  
                  <form onSubmit={handleLogin} className="space-y-4">
                    <div>
                      <Label htmlFor="login-email" className="form-label">Email</Label>
                      <Input
                        id="login-email"
                        type="email"
                        placeholder="you@company.com"
                        value={loginForm.email}
                        onChange={(e) => setLoginForm({ ...loginForm, email: e.target.value })}
                        className="form-input"
                        required
                        data-testid="login-email-input"
                      />
                    </div>
                    <div>
                      <Label htmlFor="login-password" className="form-label">Password</Label>
                      <Input
                        id="login-password"
                        type="password"
                        placeholder="••••••••"
                        value={loginForm.password}
                        onChange={(e) => setLoginForm({ ...loginForm, password: e.target.value })}
                        className="form-input"
                        required
                        data-testid="login-password-input"
                      />
                    </div>
                    <div className="flex justify-end">
                      <button
                        type="button"
                        onClick={() => setShowForgotPassword(true)}
                        className="text-sm text-blue-600 hover:text-blue-800 hover:underline"
                        data-testid="forgot-password-link"
                      >
                        Forgot password?
                      </button>
                    </div>
                    <Button 
                      type="submit" 
                      className="w-full btn-primary" 
                      disabled={isLoading}
                      data-testid="login-submit-btn"
                    >
                      {isLoading ? 'Signing in...' : 'Sign In'}
                    </Button>
                  </form>
                </TabsContent>

                <TabsContent value="register" className="mt-0">
                  {pendingRequest ? (
                    <div className="text-center py-8">
                      <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-4">
                        <Clock className="w-8 h-8 text-amber-600" />
                      </div>
                      <CardTitle className="text-2xl font-['Chivo'] mb-2">Request Submitted</CardTitle>
                      <CardDescription className="mb-6">
                        Your access request has been submitted and is pending approval. 
                        You will be able to login once an administrator approves your request.
                      </CardDescription>
                      <Button 
                        variant="outline"
                        onClick={() => setPendingRequest(false)}
                        className="mt-4"
                      >
                        Submit Another Request
                      </Button>
                    </div>
                  ) : (
                    <>
                      <CardTitle className="text-2xl font-['Chivo'] mb-2">Request Access</CardTitle>
                      <CardDescription className="mb-6">Submit your details for admin approval</CardDescription>
                      
                      <form onSubmit={handleRegister} className="space-y-4">
                        <div>
                          <Label htmlFor="register-name" className="form-label">Full Name</Label>
                          <Input
                            id="register-name"
                            type="text"
                            placeholder="John Doe"
                            value={registerForm.name}
                            onChange={(e) => setRegisterForm({ ...registerForm, name: e.target.value })}
                            className="form-input"
                            required
                            data-testid="register-name-input"
                          />
                        </div>
                        <div>
                          <Label htmlFor="register-email" className="form-label">Email</Label>
                          <Input
                            id="register-email"
                            type="email"
                            placeholder="you@company.com"
                            value={registerForm.email}
                            onChange={(e) => setRegisterForm({ ...registerForm, email: e.target.value })}
                            className="form-input"
                            required
                            data-testid="register-email-input"
                          />
                        </div>
                        <div>
                          <Label htmlFor="register-password" className="form-label">Password</Label>
                          <Input
                            id="register-password"
                            type="password"
                            placeholder="••••••••"
                            value={registerForm.password}
                            onChange={(e) => setRegisterForm({ ...registerForm, password: e.target.value })}
                            className="form-input"
                            required
                            data-testid="register-password-input"
                          />
                        </div>
                        <div>
                          <Label htmlFor="register-confirm" className="form-label">Confirm Password</Label>
                          <Input
                            id="register-confirm"
                            type="password"
                            placeholder="••••••••"
                            value={registerForm.confirmPassword}
                            onChange={(e) => setRegisterForm({ ...registerForm, confirmPassword: e.target.value })}
                            className="form-input"
                            required
                            data-testid="register-confirm-input"
                          />
                        </div>
                        <Button 
                          type="submit" 
                          className="w-full btn-primary" 
                          disabled={isLoading}
                          data-testid="register-submit-btn"
                        >
                          {isLoading ? 'Submitting...' : 'Request Access'}
                        </Button>
                      </form>
                    </>
                  )}
                </TabsContent>
              </CardContent>
            </Tabs>
          </Card>
        </div>
      </div>

      {/* Forgot Password Dialog */}
      <Dialog open={showForgotPassword} onOpenChange={setShowForgotPassword}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-['Chivo']">
              {forgotEmailSent ? 'Check Your Email' : 'Reset Password'}
            </DialogTitle>
            <DialogDescription>
              {forgotEmailSent 
                ? 'We have sent a password reset link to your email address.'
                : 'Enter your email address and we will send you a link to reset your password.'
              }
            </DialogDescription>
          </DialogHeader>
          
          {forgotEmailSent ? (
            <div className="text-center py-4">
              <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Mail className="w-8 h-8 text-emerald-600" />
              </div>
              <p className="text-sm text-slate-600 mb-4">
                If an account exists for <strong>{forgotEmail}</strong>, you will receive a password reset link shortly.
              </p>
              <Button 
                variant="outline" 
                onClick={() => {
                  setShowForgotPassword(false);
                  setForgotEmailSent(false);
                  setForgotEmail('');
                }}
                className="mt-2"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back to Login
              </Button>
            </div>
          ) : (
            <form onSubmit={handleForgotPassword} className="space-y-4">
              <div>
                <Label htmlFor="forgot-email" className="form-label">Email Address</Label>
                <Input
                  id="forgot-email"
                  type="email"
                  placeholder="you@company.com"
                  value={forgotEmail}
                  onChange={(e) => setForgotEmail(e.target.value)}
                  className="form-input"
                  required
                  data-testid="forgot-email-input"
                />
              </div>
              <div className="flex gap-3">
                <Button 
                  type="button" 
                  variant="outline" 
                  onClick={() => setShowForgotPassword(false)}
                  className="flex-1"
                >
                  Cancel
                </Button>
                <Button 
                  type="submit" 
                  className="flex-1 btn-primary" 
                  disabled={isLoading}
                  data-testid="forgot-submit-btn"
                >
                  {isLoading ? 'Sending...' : 'Send Reset Link'}
                </Button>
              </div>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Login;
